import React from 'react';
import { Link } from 'react-router-dom';
import { Leaf, MapPin, Phone, Mail, Facebook, Instagram, Linkedin, Cookie } from 'lucide-react';
import { useCookieConsent } from '../context/CookieConsentContext';

const contactInfo = {
  address: "110 Ankpa Ayingba Road, Kogi, Nigeria",
  phone: "+2349067221519",
  email: "sale@dayefarmltd.com"
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { openPreferences } = useCookieConsent();

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                <Leaf className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-bold">Daye Farm Ltd.</span>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              Sustainable integrated farming for a better future. Quality livestock, crops, and innovative agricultural practices.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61581780722231"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors duration-300"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a 
                href="https://www.instagram.com/dayefarmltd/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors duration-300"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/dayefarm/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors duration-300"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-gray-400 hover:text-green-400 transition-colors duration-300">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/ecosystem" className="text-gray-400 hover:text-green-400 transition-colors duration-300">
                  Farming Ecosystem
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-gray-400 hover:text-green-400 transition-colors duration-300">
                  Gallery
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6">Our Services</h3>
            <ul className="space-y-3">
              <li className="text-gray-400">Cattle Rearing</li>
              <li className="text-gray-400">Crop Farming</li>
              <li className="text-gray-400">Mushroom Cultivation</li>
              <li className="text-gray-400">Cashew Plantation</li>
              <li className="text-gray-400">Product Distribution</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-green-400 flex-shrink-0 mt-1" />
                <span className="text-gray-400 text-sm leading-relaxed">{contactInfo.address}</span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-green-400 flex-shrink-0" />
                <a href={`tel:${contactInfo.phone}`} className="text-gray-400 hover:text-green-400 transition-colors duration-300">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-green-400 flex-shrink-0" />
                <a href={`mailto:${contactInfo.email}`} className="text-gray-400 hover:text-green-400 transition-colors duration-300 break-all">
                  {contactInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            © {currentYear} Daye Farm Ltd. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 md:mt-0">
            <Link to="/legal/privacy" className="text-gray-400 hover:text-green-400 text-sm transition-colors duration-300" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/legal/terms" className="text-gray-400 hover:text-green-400 text-sm transition-colors duration-300" data-testid="footer-terms-link">
              Terms of Service
            </Link>
            <Link to="/legal/cookies" className="text-gray-400 hover:text-green-400 text-sm transition-colors duration-300" data-testid="footer-cookies-link">
              Cookie Notice
            </Link>
            <button
              type="button"
              onClick={openPreferences}
              className="text-gray-400 hover:text-green-400 text-sm transition-colors duration-300 inline-flex items-center gap-1"
              data-testid="footer-manage-cookies-btn"
            >
              <Cookie className="h-3.5 w-3.5" /> Manage Cookies
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;